import React, { createContext, useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TwConfirmDialog, TwNotificationDialog } from "../components/tailwind";
import { loadingState } from "../redux/reducers/loadingReducer";
import { useTranslation } from "react-i18next";

// Create Context
const ToastContext = createContext();

// Toast Provider Component
export const ToastProvider = ({ children }) => {
     const { t, i18n } = useTranslation();
     const dispatch = useDispatch();
     const loadingReduxState = useSelector(loadingState);

     /**
      * Toasts Dialog Content
      */
     const [toasts, setToasts] = useState([]);

     // Function to add a toast default, success, error, info, warning
     const addToast = (message, type = "default", option = {}) => {
          const id = Date.now();
          setToasts([...toasts, { id, message, type }]);

          // Auto-remove after 3 seconds
          setTimeout(() => removeToast(id), 3000);
     };

     const addErrorToast = (message, option = {}) => {
          const safeMessage = (typeof message === "object" && message !== null)
               ? (message.reason || message.message || JSON.stringify(message))
               : (message || "Something went wrong");
          addToast(safeMessage, "error")
     };

     const addSuccessToast = (message, option = {}) => {
          addToast(message, "success")
     };

     // Function to remove a toast
     const removeToast = (id) => {
          setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
     };

     /**
      * Notification Dialog Content
      */

     const defaultNotificationDialogContent = {
          dialogOpen: false,
          dialogType: "info", /** success, error, info, warning **/
          dialogTitle: "",
          dialogMessage: "",
          dialogBtnTitle: t("inputFieldsTitle.close"),
          dialogBtnClick: async () => { closeNotificationDialog() },
     }
     const [notificationDialogContent, setNotificationDialogContent] = useState(defaultNotificationDialogContent)

     const updateNotificationDialog = (name, value) => {
          setNotificationDialogContent((prevValues) => ({
               ...prevValues,
               [name]: value,
          }));
     };

     const addNotificationDialog = (option = defaultNotificationDialogContent) => {
          option.dialogOpen = true;
          Object.keys(option).map((c) => {
               updateNotificationDialog(c, option[c])
               return c
          })
     };

     const closeNotificationDialog = () => {
          updateNotificationDialog("dialogOpen", false)
     };


     /**
      * Confirm Dialog Content
      */

     const defaultConfirmDialogContent = {
          id: 1,
          dialogOpen: false,
          dialogType: "error", /** success, error, info, warning **/
          dialogTitle: "",
          dialogMessage: "",
          onClose: async () => { closeNotificationDialog() },
          onConfirm: async () => { closeNotificationDialog() },
     }

     const [confirmDialogContent, setConfirmDialogContent] = useState([]);

     const addConfirmDialog = (option = defaultConfirmDialogContent) => {
          setTimeout(() => {
               let newlist = [...confirmDialogContent, { ...option, id: (Math.random() * 100) + 1, dialogOpen: true }]

               setConfirmDialogContent(newlist);
          }, 1000);
     };

     return (
          <ToastContext.Provider value={{ addToast, addErrorToast, addSuccessToast, addNotificationDialog, closeNotificationDialog, addConfirmDialog }}>
               {children}

               {/* Toast Container */}
               <div className="fixed top-24 right-5 z-[10000] flex flex-col space-y-2">
                    {toasts.map((toast) => {
                         let testAndBg = toast.type === "success"
                              ? "bg-green-50 text-green-800"
                              : toast.type === "error"
                                   ? "bg-red-50 text-red-800"
                                   : toast.type === "warning"
                                        ? "bg-yellow-50 text-yellow-800"
                                        : toast.type === "info"
                                             ? "bg-blue-50 text-blue-800"
                                             : "bg-white !text-gray-500 ring-1 ring-gray-500"

                         testAndBg = toast.type === "success"
                              ? "green"
                              : toast.type === "error"
                                   ? "red"
                                   : toast.type === "warning"
                                        ? "yellow"
                                        : toast.type === "info"
                                             ? "blue"
                                             : "gray"

                         return (
                              <div key={toast.id} className={`rounded-md p-4 bg-${testAndBg}-50 text-${testAndBg}-800`}>
                                   <div className="flex">
                                        <div className="shrink-0">
                                             {toast.type === "success"
                                                  ? (<svg className={`size-5 text-green-400`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                                                       <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
                                                  </svg>)
                                                  : toast.type === "error"
                                                       ? (<svg className="size-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z" clipRule="evenodd" />
                                                       </svg>)
                                                       : toast.type === "warning"
                                                            ? (<svg className="size-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                                                                 <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
                                                            </svg>
                                                            )
                                                            : toast.type === "info"
                                                                 ? (<svg className="size-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                                                                      <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
                                                                 </svg>)
                                                                 : ""}
                                        </div>
                                        <div className="ml-3">
                                             <p className={`text-sm font-medium text-${testAndBg}-800`}>{toast.message}</p>
                                        </div>
                                        <div className="ml-auto pl-3">
                                             <div className="-mx-1.5 -my-1.5">
                                                  <button type="button" onClick={() => removeToast(toast.id)} className={`inline-flex rounded-md bg-${testAndBg}-50 p-1.5 text-${testAndBg}-500 hover:bg-${testAndBg}-100 focus:outline-none focus:ring-2 focus:ring-${testAndBg}-600 focus:ring-offset-2 focus:ring-offset-${testAndBg}-50`}>
                                                       <span className="sr-only">Dismiss</span>
                                                       <svg className="size-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                                                            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                                                       </svg>
                                                  </button>
                                             </div>
                                        </div>
                                   </div>
                              </div>

                         )
                    })}
               </div>

               <TwNotificationDialog
                    notificationDialogContent={notificationDialogContent}
                    dialogBackdropClick={async () => {
                         if (notificationDialogContent.dialogBackdropClick) {
                              await notificationDialogContent.dialogBackdropClick()
                         } else if (notificationDialogContent.dialogBtnClick) {
                              await notificationDialogContent.dialogBtnClick()
                         } else {
                         }
                         closeNotificationDialog()
                    }}
                    closeNotificationDialog={closeNotificationDialog}
                    setNotificationDialogContent={setNotificationDialogContent}
                    updateNotificationDialog={updateNotificationDialog}
               />
               <div id="confirm-dialog">
                    {confirmDialogContent.map((dialogContent, i) => {
                         return (<TwConfirmDialog
                              key={i}
                              {...dialogContent}
                         />)
                    })}
               </div>
          </ToastContext.Provider>
     );
};

// Custom Hook to use Toast
export const useToast = () => {
     return useContext(ToastContext);
};
